// ZoomPanWrapper — wraps the per-side video (and PR9's SVG overlay) in a
// pan/zoom transform with a reset-zoom button. Spec §7.2.
//
// The inner content is sized via `aspectRatio` derived from
// `folderMeta.imageSize` so the future SVG overlay shares the video's
// coordinate space pixel-for-pixel. `panningDisabled` is forwarded so PR10
// can suspend panning during keypoint drag.

import * as React from 'react';
import {
  type ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { cn } from '../../../lib/cn';

export interface ZoomPanWrapperProps {
  imageSize: [number, number]; // [H, W]
  panningDisabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function ZoomPanWrapper({
  imageSize,
  panningDisabled,
  className,
  children,
}: ZoomPanWrapperProps): React.ReactElement {
  const transformRef = React.useRef<ReactZoomPanPinchContentRef>(null);
  const [h, w] = imageSize;
  const aspect = h > 0 && w > 0 ? `${w} / ${h}` : '16 / 9';

  const onReset = React.useCallback(() => {
    transformRef.current?.resetTransform();
  }, []);

  return (
    <div
      className={cn(
        'relative flex h-full w-full items-center justify-center overflow-hidden',
        className
      )}
    >
      <div
        className="relative"
        style={{ aspectRatio: aspect, maxHeight: '100%', maxWidth: '100%' }}
      >
        <TransformWrapper
          ref={transformRef}
          minScale={1}
          maxScale={10}
          limitToBounds
          centerOnInit
          wheel={{ step: 0.008 }}
          panning={{ disabled: panningDisabled ?? false, velocityDisabled: true }}
          doubleClick={{ disabled: true }}
        >
          <TransformComponent
            wrapperStyle={{ width: '100%', height: '100%' }}
            contentStyle={{ width: '100%', height: '100%' }}
          >
            {children}
          </TransformComponent>
        </TransformWrapper>
        <button
          type="button"
          onClick={onReset}
          aria-label="Reset zoom"
          data-testid="reset-zoom"
          className="absolute right-2 top-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded bg-background/80 text-foreground shadow-sm ring-1 ring-border transition hover:bg-background"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <polyline points="3 4 3 10 9 10" />
          </svg>
        </button>
      </div>
    </div>
  );
}
